import { cloneDeep, get, pick } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  useCreateRemoteLayoutMutation,
  useUpdateRemoteLayoutMutation,
} from 'services/api/remoteLayout';
import { RootState } from 'types';
import { track } from 'analytics/posthog';
import { LayoutFormStyles } from './style';
import ControlSection from '../ControlSection';

const initialState = {
  name: '',
  sections: [
    {
      name: 'Controls',
      values: [] as any,
    },
  ],
};

function LayoutForm({ show, onHide, action, data }) {
  const [layout, setLayout] = useState(initialState);
  const user = pick(
    useSelector((state: RootState) => state.session.user),
    ['email', 'name'],
  );

  const [createLayout, createLayoutState] = useCreateRemoteLayoutMutation();
  const [updateLayout, updateLayoutState] = useUpdateRemoteLayoutMutation();

  const handleSubmit = () => {
    if (action === 'edit') {
      updateLayout({ ...layout, metadata: { lastModifiedBy: user } });
    } else {
      createLayout({
        ...layout,
        metadata: { createdBy: user, lastModifiedBy: user },
      });
    }
  };

  const handleSectionEdit = (action, values, index = -1) => {
    setLayout(prev => {
      const newLayout = cloneDeep(prev);
      if (action === 'add') {
        newLayout.sections.push({ ...values, values: [] });
        return newLayout;
      }
      if (action === 'delete') {
        newLayout.sections.splice(index, 1);
        return newLayout;
      }
      if (action === 'edit') {
        newLayout.sections[index] = { ...newLayout.sections[index], ...values };
        return newLayout;
      }
      return newLayout;
    });
  };

  const handleCommandEdit = (sectionName, action, values, index) => {
    setLayout(prev => {
      const newLayout = cloneDeep(prev);
      const section = newLayout.sections.find(el => el.name === sectionName);
      if (!section) return newLayout;

      if (action === 'add') {
        section.values.push(
          typeof values === 'string' ? values : cloneDeep(values),
        );
        return newLayout;
      }
      if (action === 'delete') {
        section.values.splice(index, 1);
        return newLayout;
      }
      if (action === 'edit') {
        section.values[index] =
          typeof values === 'string' ? values : cloneDeep(values);
        return newLayout;
      }
      return newLayout;
    });
  };

  const moveSection = (initialIndex, finalIndex) => {
    if (finalIndex < 1) return;

    setLayout(prev => {
      const newLayout = cloneDeep(prev);
      const section = newLayout.sections.splice(initialIndex, 1);
      newLayout.sections.splice(finalIndex, 0, ...section);
      return newLayout;
    });
  };

  const moveCommand = (
    initialSectionName,
    initialIndex,
    finalSectionName,
    finalIndex,
  ) => {
    if (finalIndex < 0) return;
    setLayout(prev => {
      const newLayout = cloneDeep(prev);
      const initialSection = newLayout.sections.find(
        section => section.name === initialSectionName,
      );
      if (!initialSection) return newLayout;
      const command = initialSection.values.splice(initialIndex, 1);

      if (typeof command[0] === 'string' && finalSectionName !== 'Controls') {
        initialSection.values.splice(initialIndex, 0, ...command);
        return newLayout;
      }

      const finalSection = newLayout.sections.find(
        section => section.name === finalSectionName,
      );
      if (!finalSection) return newLayout;
      finalSection.values.splice(finalIndex, 0, ...command);

      return newLayout;
    });
  };

  const handleOnDragEnd = result => {
    if (result.destination === null) return;
    moveCommand(
      result.source.droppableId.split(':')[0],
      result.source.index,
      result.destination.droppableId.split(':')[0],
      result.destination.index,
    );
  };

  useEffect(() => {
    if (action === 'new') setLayout(initialState);
    else setLayout(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, action]);

  useEffect(() => {
    if (createLayoutState.isSuccess) {
      if (action === 'clone') {
        track('remote layout cloned', {
          source_layout_name: data?.name,
        });
      } else {
        track('remote layout created', {
          layout_name: layout.name,
        });
      }
      toast.success('Remote layout created successfully');
      onHide();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createLayoutState.isSuccess]);

  useEffect(() => {
    if (updateLayoutState.isSuccess) {
      track('remote layout edited', {
        layout_name: layout.name,
      });
      toast.success('Remote layout created successfully');
      onHide();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateLayoutState.isSuccess]);

  useEffect(() => {
    if (createLayoutState.isError)
      toast.error(get(createLayoutState, 'error.data.error'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createLayoutState.isError]);

  useEffect(() => {
    if (updateLayoutState.isError)
      toast.error(get(updateLayoutState, 'error.data.error'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateLayoutState.isError]);

  return (
    <LayoutFormStyles show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {action === 'edit' ? 'Edit Remote Layout' : 'Create Remote Layout'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ControlSection
          action={action}
          layout={layout}
          handleOnDragEnd={handleOnDragEnd}
          moveSection={moveSection}
          moveCommand={moveCommand}
          onSectionEdit={handleSectionEdit}
          onCommandEdit={handleCommandEdit}
          onNameEdit={name => setLayout(prev => ({ ...prev, name }))}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button
          disabled={
            createLayoutState.isLoading ||
            updateLayoutState.isLoading ||
            !layout.name
          }
          onClick={handleSubmit}
        >
          Save
        </Button>
        <Button variant="danger" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </LayoutFormStyles>
  );
}

export default LayoutForm;
